import React, { useState } from "react";
import {
  Paper,
  Title,
  Button,
  Group,
  Stack,
  Loader,
  Alert,
  Modal,
  Text,
  Select,
} from "@mantine/core";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { IconPlus, IconAlertCircle } from "@tabler/icons-react";
import {
  getCampaigns,
  startCampaign,
  pauseCampaign,
  deleteCampaign,
} from "../../../../../api/outreach/email";
import {
  EmailOutreachQueryNames,
  CampaignStatus,
} from "../../../../../api/requests_responses/outreach/email";
import EmailCampaignCard from "./EmailCampaignCard";

const EmailCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [campaignToAction, setCampaignToAction] = useState<{
    id: number;
    action: "start" | "pause" | "resume" | "delete";
  } | null>(null);

  const {
    data: campaignsResponse,
    isLoading,
    error,
  } = useQuery(
    [EmailOutreachQueryNames.GET_CAMPAIGNS, statusFilter],
    () => getCampaigns(statusFilter ? { status: statusFilter } : undefined),
    {
      refetchOnWindowFocus: false,
    },
  );

  const startMutation = useMutation(
    (campaignId: number) => startCampaign(campaignId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(EmailOutreachQueryNames.GET_CAMPAIGNS);
        queryClient.invalidateQueries(EmailOutreachQueryNames.GET_CAMPAIGN);
        setActionModalOpen(false);
        setCampaignToAction(null);
      },
    },
  );

  const pauseMutation = useMutation(
    (campaignId: number) => pauseCampaign(campaignId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(EmailOutreachQueryNames.GET_CAMPAIGNS);
        queryClient.invalidateQueries(EmailOutreachQueryNames.GET_CAMPAIGN);
        setActionModalOpen(false);
        setCampaignToAction(null);
      },
    },
  );

  const resumeMutation = useMutation(
    (campaignId: number) => startCampaign(campaignId), // Resume uses the same endpoint as start
    {
      onSuccess: () => {
        queryClient.invalidateQueries(EmailOutreachQueryNames.GET_CAMPAIGNS);
        queryClient.invalidateQueries(EmailOutreachQueryNames.GET_CAMPAIGN);
        setActionModalOpen(false);
        setCampaignToAction(null);
      },
    },
  );

  const deleteMutation = useMutation(
    (campaignId: number) => deleteCampaign(campaignId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(EmailOutreachQueryNames.GET_CAMPAIGNS);
        setDeleteModalOpen(false);
        setCampaignToAction(null);
      },
    },
  );

  const handleCreateClick = () => {
    navigate("/outreach/email/campaigns/create");
  };

  const handleViewClick = (campaignId: number) => {
    navigate(`/outreach/email/campaigns/${campaignId}`);
  };

  const handleEditClick = (campaignId: number) => {
    navigate(`/outreach/email/campaigns/${campaignId}/edit`);
  };

  const handleStartClick = (campaignId: number) => {
    setCampaignToAction({ id: campaignId, action: "start" });
    setActionModalOpen(true);
  };

  const handlePauseClick = (campaignId: number) => {
    setCampaignToAction({ id: campaignId, action: "pause" });
    setActionModalOpen(true);
  };

  const handleResumeClick = (campaignId: number) => {
    setCampaignToAction({ id: campaignId, action: "resume" });
    setActionModalOpen(true);
  };

  const handleDeleteClick = (campaignId: number) => {
    setCampaignToAction({ id: campaignId, action: "delete" });
    setDeleteModalOpen(true);
  };

  const handleActionConfirm = () => {
    if (!campaignToAction) return;

    if (campaignToAction.action === "start") {
      startMutation.mutate(campaignToAction.id);
    } else if (campaignToAction.action === "pause") {
      pauseMutation.mutate(campaignToAction.id);
    } else if (campaignToAction.action === "resume") {
      resumeMutation.mutate(campaignToAction.id);
    }
  };

  const handleDeleteConfirm = () => {
    if (campaignToAction?.action === "delete") {
      deleteMutation.mutate(campaignToAction.id);
    }
  };

  if (isLoading) {
    return (
      <Paper p="md" shadow="sm">
        <Loader />
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        Failed to load campaigns. Please try again.
      </Alert>
    );
  }

  const campaigns = campaignsResponse?.data?.campaigns || [];

  return (
    <>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3}>Email Campaigns</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateClick}
          >
            Create Campaign
          </Button>
        </Group>

        <Select
          placeholder="Filter by status"
          data={[
            { value: "", label: "All" },
            { value: CampaignStatus.DRAFT, label: "Draft" },
            { value: CampaignStatus.SCHEDULED, label: "Scheduled" },
            { value: CampaignStatus.RUNNING, label: "Running" },
            { value: CampaignStatus.PAUSED, label: "Paused" },
            { value: CampaignStatus.COMPLETED, label: "Completed" },
            { value: CampaignStatus.FAILED, label: "Failed" },
          ]}
          value={statusFilter || ""}
          onChange={(value) => setStatusFilter(value || null)}
          clearable
        />

        {campaigns.length === 0 ? (
          <Paper p="xl" shadow="sm" withBorder>
            <Stack align="center" gap="md">
              <Text size="lg" c="dimmed" ta="center">
                No campaigns yet
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                Create your first email campaign to start reaching out to
                contacts
              </Text>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={handleCreateClick}
              >
                Create Your First Campaign
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Group>
            {campaigns.map((campaign) => (
              <EmailCampaignCard
                key={campaign.id}
                campaign={campaign}
                onView={handleViewClick}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onStart={handleStartClick}
                onPause={handlePauseClick}
                onResume={handleResumeClick}
              />
            ))}
          </Group>
        )}
      </Stack>

      <Modal
        opened={actionModalOpen}
        onClose={() => setActionModalOpen(false)}
        title={
          campaignToAction?.action === "start"
            ? "Start Campaign"
            : campaignToAction?.action === "pause"
            ? "Pause Campaign"
            : "Resume Campaign"
        }
      >
        <Text mb="md">
          Are you sure you want to {campaignToAction?.action} this campaign?
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={() => setActionModalOpen(false)}>
            Cancel
          </Button>
          <Button
            color={campaignToAction?.action === "pause" ? "yellow" : "green"}
            onClick={handleActionConfirm}
            loading={
              startMutation.isLoading ||
              pauseMutation.isLoading ||
              resumeMutation.isLoading
            }
          >
            {campaignToAction?.action === "start"
              ? "Start"
              : campaignToAction?.action === "pause"
              ? "Pause"
              : "Resume"}
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Campaign"
      >
        <Text mb="md">
          Are you sure you want to delete this campaign? This action cannot be
          undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDeleteConfirm}
            loading={deleteMutation.isLoading}
          >
            Delete
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default EmailCampaigns;
